import type { History, LocationDescriptor } from "history";
import { useEffect, useState } from "react";
import type { HistoryUnsubscribe } from "react-router-redux";
import { useLocation } from "react-use";

import { useIsValidSidekickClientEmbedOrigin } from "./useIsValidSidekickClientEmbedOrigin";

type SidekickHistoryMessage = {
  type: "sk.historyChanged";
  pathname: string;
  search: string;
  hash: string;
};

const SIDEKICK_LOCATION_STATE_KEY = "fromSidekick";

const isHistoryMessage = (value: unknown): value is SidekickHistoryMessage => {
  if (typeof value !== "object" || value == null) {
    return false;
  }

  const message = value as Record<string, unknown>;
  return (
    message.type === "sk.historyChanged" &&
    typeof message.pathname === "string" &&
    typeof message.search === "string" &&
    typeof message.hash === "string"
  );
};

const isHandshakeMessage = (value: unknown): boolean => {
  if (typeof value !== "object" || value == null) {
    return false;
  }

  return (value as Record<string, unknown>).type === "sk.historyHandshake";
};

export const useLocationAndHistorySync = (
  history: History & HistoryUnsubscribe,
) => {
  const isValidSidekickClientEmbedOrigin =
    useIsValidSidekickClientEmbedOrigin();
  const [trustedOrigin, setTrustedOrigin] = useState<string | null>(null);
  const location = useLocation();

  // Listen for messages and set the location accordingly if the message is from a trusted origin
  // Also, if we receive a handshake message from an untrusted origin, set that origin as trusted if it's valid
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.source !== window.parent) {
        return;
      }

      if (
        trustedOrigin == null &&
        isHandshakeMessage(event.data) &&
        isValidSidekickClientEmbedOrigin(event.origin)
      ) {
        setTrustedOrigin(event.origin);
        return;
      }

      if (trustedOrigin === event.origin && isHistoryMessage(event.data)) {
        const message = event.data;
        const newLocation: LocationDescriptor = {
          pathname: message.pathname,
          search: message.search,
          hash: message.hash,
          state: { [SIDEKICK_LOCATION_STATE_KEY]: true },
        };
        history.replace(newLocation);
      }
    };
    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [trustedOrigin, isValidSidekickClientEmbedOrigin, history]);

  // Notify the parent window of history changes
  useEffect(() => {
    if (trustedOrigin) {
      const unlisten = history.listenBefore((location, callback) => {
        if (
          typeof location.state === "object" &&
          location.state != null &&
          (location.state as Record<string, unknown>)[
            SIDEKICK_LOCATION_STATE_KEY
          ] === true
        ) {
          callback(true);
          return;
        }

        window.parent.postMessage(
          {
            type: "mb.historyChanged",
            ...location,
          },
          trustedOrigin,
        );
        callback(false);
      });
      return () => {
        unlisten();
      };
    }
  }, [trustedOrigin, history]);

  // Notify the parent window of search and hash changes
  useEffect(() => {
    if (trustedOrigin) {
      window.parent.postMessage(
        {
          type: "mb.searchOrHashChanged",
          search: location.search,
          hash: location.hash,
        },
        trustedOrigin,
      );
    }
  }, [location.search, location.hash, trustedOrigin]);
};
